import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import '../styles/App.css'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001'

function Landing() {
  const [isVisible, setIsVisible] = useState(false)
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 })

  useEffect(() => {
    setIsVisible(true)

    const handleMouseMove = (e) => {
      setMousePos({ x: e.clientX, y: e.clientY })
    }

    window.addEventListener('mousemove', handleMouseMove)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
    }
  }, [])

  const handleLogin = () => {
    window.location.href = `${API_URL}/twitch/terrarium/auth/login`
  }

  const features = [
    {
      icon: '🌱',
      title: 'Grow Plants',
      description: 'Each Twitch viewer grows their own plant in a shared terrarium using free chat commands',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    },
    {
      icon: '🐛',
      title: 'Adopt Insects',
      description: 'Viewers choose to be a cute insect—ladybugs, butterflies, bees, moths, scorpions, and more',
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)'
    },
    {
      icon: '💬',
      title: 'Chat Bubbles',
      description: 'When viewers chat, speech bubbles appear above their plant or insect in real-time',
      gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
    },
    {
      icon: '🎉',
      title: 'Fun Interactions',
      description: 'Dance, sing, pet, sting, nuzzle, give hats, shake plants—30+ ways to play',
      gradient: 'linear-gradient(135deg, #ec4899 0%, #f97316 100%)'
    },
    {
      icon: '🔄',
      title: 'Persistent Growth',
      description: 'Plants keep growing between streams—your community\'s terrarium is always alive, 24/7',
      gradient: 'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)'
    },
    {
      icon: '🎨',
      title: 'Fully Customizable',
      description: 'Toggle ground, glass frame, chat bubbles, notifications, and more from the settings panel',
      gradient: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)'
    }
  ]

  const plantTypes = ['Flower', 'Mushroom', 'Cactus', 'Sunflower', 'Tulip', 'Rose', 'Tree', 'Fern', 'Succulent', 'Bamboo', 'Vine', 'Bonsai', 'Cherry Blossom', 'Crystal Tree', 'Venus Flytrap']
  const insectTypes = ['Ladybug', 'Butterfly', 'Bee', 'Caterpillar', 'Firefly', 'Beetle', 'Dragonfly', 'Snail', 'Worm', 'Moth', 'Mantis', 'Scorpion']

  return (
    <div className="landing-2026">
      {/* Animated Background */}
      <div className="bg-aurora">
        <div className="aurora-blob aurora-1"></div>
        <div className="aurora-blob aurora-2"></div>
        <div className="aurora-blob aurora-3"></div>
        <div className="noise-overlay"></div>
      </div>

      {/* Floating Particles */}
      <div className="particles">
        {[...Array(20)].map((_, i) => (
          <div key={i} className="particle" style={{
            '--delay': `${i * 0.5}s`,
            '--x': `${Math.random() * 100}%`,
            '--size': `${Math.random() * 8 + 4}px`
          }}></div>
        ))}
      </div>

      {/* Navigation */}
      <nav className="nav-glass">
        <div className="nav-brand">
          <div className="brand-icon">
            <svg viewBox="0 0 32 32" fill="none">
              {/* Glass jar body */}
              <path d="M7 10h18v1c0 9-4 16-9 16S7 20 7 11v-1z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" opacity="0.5"/>
              {/* Jar rim */}
              <rect x="9" y="8" width="14" height="2.5" rx="1.2" stroke="currentColor" strokeWidth="1.5" opacity="0.6"/>
              {/* Soil */}
              <path d="M9 22c0 0 3 2 7 2s7-2 7-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.4"/>
              {/* Plant stem */}
              <path d="M16 22v-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
              {/* Leaves */}
              <path d="M16 18c-2-1-4 0-4-3 2 0 3.5 1 4 3z" fill="currentColor" opacity="0.8"/>
              <path d="M16 16c2-1 4 0 4-3-2 0-3.5 1-4 3z" fill="currentColor"/>
              {/* Flower */}
              <circle cx="16" cy="13" r="2" fill="currentColor" opacity="0.9"/>
              <circle cx="16" cy="13" r="0.8" fill="#050a0f"/>
              {/* Tiny butterfly */}
              <path d="M22 14l-1.5 1 1.5 1" stroke="currentColor" strokeWidth="0.8" strokeLinecap="round" opacity="0.6"/>
              <path d="M22 14l1.5 1-1.5 1" stroke="currentColor" strokeWidth="0.8" strokeLinecap="round" opacity="0.6"/>
            </svg>
          </div>
          <span className="brand-text">Stream Terrarium</span>
        </div>
        <div className="nav-actions">
          <a href="#features" className="nav-link">Features</a>
          <a href="#how-it-works" className="nav-link">How It Works</a>
          <a href="#faq" className="nav-link">FAQ</a>
          <Link to="/help" className="nav-link">Help</Link>
          <button className="nav-cta" onClick={handleLogin}>
            <span>Launch App</span>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className={`hero-2026 ${isVisible ? 'visible' : ''}`}>
        <div className="hero-content">
          <div className="hero-badge-wrapper">
            <div className="hero-badge">
              <span className="badge-dot"></span>
              <span>Interactive Twitch Overlay</span>
            </div>
          </div>

          <h1 className="hero-headline">
            <span className="line-1">Free Interactive</span>
            <span className="line-2">
              <span className="gradient-text">Twitch Terrarium</span>
            </span>
            <span className="line-3">For Your Stream</span>
          </h1>

          <p className="hero-description">
            The free Twitch overlay where every viewer grows plants, adopts insects, and builds a living
            terrarium together. Dance, sing, pet, sting, nuzzle, give hats—30+ commands with live chat bubbles.
            Setup in 60 seconds, free forever.
          </p>

          <div className="hero-cta-group">
            <button className="cta-glow" onClick={handleLogin}>
              <span className="cta-content">
                <span className="cta-text">Start Growing Free</span>
                <span className="cta-arrow">→</span>
              </span>
              <div className="cta-shimmer"></div>
            </button>
            {/* <a href="#demo" className="cta-outline">
              <span>Watch Demo</span>
              <svg viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M10 8l6 4-6 4V8z" fill="currentColor"/>
              </svg>
            </a> */}
          </div>

          <div className="hero-social-proof">
            <div className="avatars">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="avatar" style={{ '--i': i }}>
                  <div className="avatar-inner"></div>
                </div>
              ))}
            </div>
            <div className="proof-text">
              <span className="proof-number">1,200+</span>
              <span className="proof-label">streamers growing gardens</span>
            </div>
          </div>
        </div>

        {/* 3D Terrarium Preview */}
        <div className="hero-visual">
          <div className="terrarium-showcase" style={{
            transform: `perspective(1000px) rotateY(${(mousePos.x - window.innerWidth / 2) * 0.01}deg) rotateX(${(mousePos.y - window.innerHeight / 2) * -0.01}deg)`
          }}>
            <div className="showcase-glow"></div>
            <div className="showcase-jar">
              <div className="jar-reflection"></div>
              <div className="jar-content">
                <div className="mini-sky">
                  <div className="mini-cloud c1"></div>
                  <div className="mini-cloud c2"></div>
                </div>
                <div className="mini-plant p1">
                  <div className="plant-stem"></div>
                  <div className="plant-leaves">
                    <div className="leaf l1"></div>
                    <div className="leaf l2"></div>
                    <div className="leaf l3"></div>
                  </div>
                </div>
                <div className="mini-plant p2">
                  <div className="mushroom-cap"></div>
                  <div className="mushroom-stem"></div>
                </div>
                <div className="mini-plant p3">
                  <div className="flower-head">
                    <div className="flower-petals">
                      {[...Array(5)].map((_, i) => (
                        <div key={i} className="petal" style={{ '--i': i }}></div>
                      ))}
                    </div>
                    <div className="flower-center"></div>
                  </div>
                  <div className="flower-stem"></div>
                </div>
                <div className="mini-insect insect-butterfly">
                  <div className="mini-insect-wing wing-l"></div>
                  <div className="mini-insect-body"></div>
                  <div className="mini-insect-wing wing-r"></div>
                </div>
                <div className="mini-insect insect-ladybug">
                  <div className="mini-ladybug-body">
                    <div className="mini-ladybug-spot"></div>
                    <div className="mini-ladybug-spot s2"></div>
                  </div>
                </div>
                <div className="mini-chat-bubble bubble-1">
                  <span>hello!</span>
                </div>
                <div className="mini-chat-bubble bubble-2">
                  <span>nice garden</span>
                </div>
                <div className="mini-ground">
                  <div className="ground-moss"></div>
                </div>
              </div>
            </div>
            <div className="floating-labels">
              <div className="label label-1">
                <span className="label-icon">🦋</span>
                <span className="label-text">butterfly is dancing!</span>
              </div>
              <div className="label label-2">
                <span className="label-icon">💬</span>
                <span className="label-text">chat bubbles live</span>
              </div>
              <div className="label label-3">
                <span className="label-icon">🌿</span>
                <span className="label-text">plant is shaking!</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="stats-bar" aria-label="Stream Terrarium stats">
        <div className="stats-container">
          <div className="stat-item">
            <span className="stat-number">{plantTypes.length}</span>
            <span className="stat-label">Plant Species</span>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item">
            <span className="stat-number">{insectTypes.length}</span>
            <span className="stat-label">Insect Types</span>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item">
            <span className="stat-number">30+</span>
            <span className="stat-label">Chat Commands</span>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item">
            <span className="stat-number">$0</span>
            <span className="stat-label">Free Forever</span>
          </div>
        </div>
      </section>

      {/* Features Bento Grid */}
      <section id="features" className="features-bento">
        <div className="section-intro">
          <span className="section-tag">Features</span>
          <h2 className="section-title">Everything your stream needs</h2>
          <p className="section-subtitle">A complete interactive experience that brings your community together</p>
        </div>

        <div className="bento-grid">
          {features.map((feature, index) => (
            <div key={index} className={`bento-card card-${index + 1}`}>
              <div className="card-glow" style={{ background: feature.gradient }}></div>
              <div className="card-content">
                <span className="card-icon">{feature.icon}</span>
                <h3 className="card-title">{feature.title}</h3>
                <p className="card-description">{feature.description}</p>
              </div>
            </div>
          ))}

          {/* Special Command Card */}
          <div className="bento-card card-commands">
            <div className="card-content">
              <span className="card-icon">⌨️</span>
              <h3 className="card-title">30+ Free Chat Commands</h3>
              <div className="command-list">
                <div className="command-item">
                  <code>!join plant flower</code>
                  <span>Grow a plant</span>
                </div>
                <div className="command-item">
                  <code>!join insect butterfly</code>
                  <span>Adopt an insect</span>
                </div>
                <div className="command-item">
                  <code>!water @user</code>
                  <span>Help plants grow</span>
                </div>
                <div className="command-item">
                  <code>!fly</code>
                  <span>Fly to a random spot</span>
                </div>
                <div className="command-item">
                  <code>!pollinate @user</code>
                  <span>Boost a plant's growth</span>
                </div>
                <div className="command-item">
                  <code>!visit @user</code>
                  <span>Visit another entity</span>
                </div>
                <div className="command-item">
                  <code>!dance</code>
                  <span>Make your insect dance</span>
                </div>
                <div className="command-item">
                  <code>!pet @user</code>
                  <span>Pet an insect with hearts</span>
                </div>
                <div className="command-item">
                  <code>!sing</code>
                  <span>Sing with music notes</span>
                </div>
                <div className="command-item">
                  <code>!nuzzle @user</code>
                  <span>Nuzzle up to a friend</span>
                </div>
                <div className="command-item">
                  <code>!shake</code>
                  <span>Shake your plant</span>
                </div>
                <div className="command-item">
                  <code>!bloom</code>
                  <span>Bloom at full growth</span>
                </div>
                <div className="command-item">
                  <code>!sting @user</code>
                  <span>Bee stings another entity</span>
                </div>
                <div className="command-item">
                  <code>!birthdayhat @user</code>
                  <span>Give a birthday hat</span>
                </div>
                <div className="command-item">
                  <code>!crown @user</code>
                  <span>Give a crown</span>
                </div>
                <div className="command-item">
                  <code>!wave</code>
                  <span>Wave at everyone</span>
                </div>
              </div>
            </div>
          </div>

          {/* Plant & Insect Types Card */}
          <div className="bento-card card-plants">
            <div className="card-content">
              <span className="card-icon">🌿</span>
              <h3 className="card-title">{plantTypes.length} Plants & {insectTypes.length} Insects</h3>
              <div className="plant-tags">
                {plantTypes.map((plant, i) => (
                  <span key={`p-${i}`} className="plant-tag">{plant}</span>
                ))}
                {insectTypes.map((insect, i) => (
                  <span key={`i-${i}`} className="plant-tag insect-tag">{insect}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="how-section">
        <div className="section-intro">
          <span className="section-tag">Setup</span>
          <h2 className="section-title">Live in under 60 seconds</h2>
          <p className="section-subtitle">Three simple steps to transform your stream</p>
        </div>

        <div className="steps-timeline">
          <div className="timeline-line">
            <div className="line-progress"></div>
          </div>

          <div className="step-card">
            <div className="step-number">01</div>
            <div className="step-content">
              <div className="step-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
                  <polyline points="10 17 15 12 10 7"/>
                  <line x1="15" y1="12" x2="3" y2="12"/>
                </svg>
              </div>
              <h3>Connect Your Twitch Account</h3>
              <p>Free one-click login. We never access your stream key or sensitive data.</p>
            </div>
          </div>

          <div className="step-card">
            <div className="step-number">02</div>
            <div className="step-content">
              <div className="step-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                </svg>
              </div>
              <h3>Copy Your Free Overlay URL</h3>
              <p>Get your unique terrarium URL instantly. Each Twitch broadcaster gets their own terrarium.</p>
            </div>
          </div>

          <div className="step-card">
            <div className="step-number">03</div>
            <div className="step-content">
              <div className="step-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <polygon points="23 7 16 12 23 17 23 7"/>
                  <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
                </svg>
              </div>
              <h3>Add to OBS Free</h3>
              <p>Paste as a Browser Source in OBS. Your interactive Twitch terrarium appears on stream immediately.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section for AEO */}
      <section id="faq" className="faq-section" itemScope itemType="https://schema.org/FAQPage">
        <div className="section-intro">
          <span className="section-tag">FAQ</span>
          <h2 className="section-title">Frequently Asked Questions</h2>
          <p className="section-subtitle">Everything you need to know about this free Twitch overlay</p>
        </div>

        <div className="faq-grid">
          <article className="faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
            <h3 itemProp="name">What is Stream Terrarium?</h3>
            <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
              <p itemProp="text">Stream Terrarium is a free interactive Twitch overlay that lets your viewers grow plants, adopt cute insects, and build a living terrarium together using simple chat commands. It works as an OBS Browser Source and requires no downloads or extensions.</p>
            </div>
          </article>

          <article className="faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
            <h3 itemProp="name">Is Stream Terrarium really free?</h3>
            <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
              <p itemProp="text">Yes, Stream Terrarium is 100% free. There are no hidden costs, no premium tiers, and no credit card required. It's free forever for all Twitch streamers.</p>
            </div>
          </article>

          <article className="faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
            <h3 itemProp="name">How do I add Stream Terrarium to my Twitch stream?</h3>
            <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
              <p itemProp="text">Setup takes under 60 seconds: connect your Twitch account with one click, copy your unique overlay URL, and paste it as a Browser Source in OBS or Streamlabs. Your terrarium appears on stream immediately.</p>
            </div>
          </article>

          <article className="faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
            <h3 itemProp="name">What can viewers do in the terrarium?</h3>
            <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
              <p itemProp="text">Viewers can join as a plant or insect using !join, water plants, fly around, pollinate flowers, and visit friends. Plus fun interactions: dance, sing, pet, sting (bees), nuzzle, give hats (birthday, crown, wizard, and more), shake plants, and bloom. When viewers type in chat, speech bubbles appear above their entity. Choose from 15 plant species and 12 insect types including butterflies, moths, scorpions, and more.</p>
            </div>
          </article>

          <article className="faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
            <h3 itemProp="name">Does the terrarium persist between streams?</h3>
            <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
              <p itemProp="text">Yes! Plants continue growing between streams. When you go live again, your community's terrarium is exactly where they left it—always growing, 24/7.</p>
            </div>
          </article>

          <article className="faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
            <h3 itemProp="name">Does Stream Terrarium work with OBS and Streamlabs?</h3>
            <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
              <p itemProp="text">Yes, Stream Terrarium works with any streaming software that supports Browser Sources, including OBS Studio, Streamlabs, and XSplit. Just paste your overlay URL and you're live.</p>
            </div>
          </article>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section-2026">
        <div className="cta-card-2026">
          <div className="cta-bg-pattern"></div>
          <div className="cta-content-2026">
            <h2>Ready to grow your community terrarium?</h2>
            <p>Free forever. No credit card. No catch. Join thousands of Twitch streamers.</p>
            <button className="cta-mega" onClick={handleLogin}>
              <span className="cta-mega-icon">🌱</span>
              <span className="cta-mega-text">Start Free on Twitch</span>
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer-2026">
        <div className="footer-content-2026">
          <div className="footer-brand">
            <div className="brand-icon small">
              <svg viewBox="0 0 32 32" fill="none">
                <path d="M7 10h18v1c0 9-4 16-9 16S7 20 7 11v-1z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" opacity="0.5"/>
                <rect x="9" y="8" width="14" height="2.5" rx="1.2" stroke="currentColor" strokeWidth="1.5" opacity="0.6"/>
                <path d="M16 22v-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                <path d="M16 18c-2-1-4 0-4-3 2 0 3.5 1 4 3z" fill="currentColor" opacity="0.8"/>
                <path d="M16 16c2-1 4 0 4-3-2 0-3.5 1-4 3z" fill="currentColor"/>
                <circle cx="16" cy="13" r="2" fill="currentColor" opacity="0.9"/>
              </svg>
            </div>
            <span>Stream Terrarium</span>
          </div>
          <div className="footer-links">
            <span>&copy; 2026 Stream Terrarium</span>
            <span className="footer-dot">·</span>
            <Link to="/terms" className="footer-legal-link">Terms of Service</Link>
            <span className="footer-dot">·</span>
            <Link to="/privacy" className="footer-legal-link">Privacy Policy</Link>
            <span className="footer-dot">·</span>
            <span>Made with 💚 for streamers</span>
          </div>
        </div>
      </footer>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');

        /* Reset & Base - Override global styles for landing page */
        html, body, #root {
          overflow: visible !important;
          overflow-y: auto !important;
          height: auto !important;
          min-height: 100vh;
        }

        .landing-2026 {
          min-height: 100vh;
          background: #050a0f;
          color: #fff;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          overflow-x: hidden;
          overflow-y: auto;
          position: relative;
        }

        /* Aurora Background */
        .bg-aurora {
          position: fixed;
          inset: 0;
          overflow: hidden;
          z-index: 0;
          pointer-events: none;
        }

        .aurora-blob {
          position: absolute;
          border-radius: 50%;
          filter: blur(100px);
          opacity: 0.4;
          animation: aurora 20s ease-in-out infinite;
        }

        .aurora-1 {
          width: 600px;
          height: 600px;
          background: linear-gradient(180deg, #10b981, #059669);
          top: -200px;
          left: -100px;
        }

        .aurora-2 {
          width: 500px;
          height: 500px;
          background: linear-gradient(180deg, #6366f1, #8b5cf6);
          top: 30%;
          right: -150px;
          animation-delay: -5s;
        }

        .aurora-3 {
          width: 400px;
          height: 400px;
          background: linear-gradient(180deg, #14b8a6, #22d3ee);
          bottom: -100px;
          left: 30%;
          animation-delay: -10s;
        }

        @keyframes aurora {
          0%, 100% { transform: translate(0, 0) scale(1); }
          25% { transform: translate(50px, 30px) scale(1.1); }
          50% { transform: translate(-30px, 60px) scale(0.95); }
          75% { transform: translate(40px, -20px) scale(1.05); }
        }

        .noise-overlay {
          position: absolute;
          inset: 0;
          background: url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
          opacity: 0.03;
          pointer-events: none;
        }

        /* Floating Particles */
        .particles {
          position: fixed;
          inset: 0;
          pointer-events: none;
          z-index: 1;
        }

        .particle {
          position: absolute;
          width: var(--size);
          height: var(--size);
          background: radial-gradient(circle, rgba(16, 185, 129, 0.6) 0%, transparent 70%);
          border-radius: 50%;
          left: var(--x);
          animation: float-up 15s linear infinite;
          animation-delay: var(--delay);
        }

        @keyframes float-up {
          0% { transform: translateY(100vh) rotate(0deg); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { transform: translateY(-100px) rotate(360deg); opacity: 0; }
        }

        /* Glass Navigation */
        .nav-glass {
          position: fixed;
          top: 20px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 24px;
          background: rgba(255, 255, 255, 0.03);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 100px;
          z-index: 1000;
          width: calc(100% - 48px);
          max-width: 900px;
        }

        .nav-brand {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .brand-icon {
          width: 32px;
          height: 32px;
          color: #10b981;
        }

        .brand-icon.small {
          width: 24px;
          height: 24px;
        }

        .brand-text {
          font-weight: 700;
          font-size: 1rem;
          letter-spacing: -0.02em;
        }

        .nav-actions {
          display: flex;
          align-items: center;
          gap: 24px;
        }

        .nav-link {
          color: rgba(255, 255, 255, 0.7);
          text-decoration: none;
          font-size: 0.875rem;
          font-weight: 500;
          transition: color 0.2s;
        }

        .nav-link:hover {
          color: #fff;
        }

        .nav-cta {
          display: flex;
          align-items: center;
          gap: 8px;
          background: linear-gradient(135deg, #10b981, #059669);
          color: #fff;
          border: none;
          padding: 10px 20px;
          border-radius: 100px;
          font-size: 0.875rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s;
        }

        .nav-cta svg {
          width: 16px;
          height: 16px;
        }

        .nav-cta:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 40px rgba(16, 185, 129, 0.3);
        }

        /* Hero Section */
        .hero-2026 {
          position: relative;
          z-index: 2;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 60px;
          align-items: center;
          min-height: 100vh;
          padding: 120px 60px 80px;
          max-width: 1400px;
          margin: 0 auto;
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .hero-2026.visible {
          opacity: 1;
          transform: translateY(0);
        }

        .hero-badge-wrapper {
          margin-bottom: 24px;
        }

        .hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: rgba(16, 185, 129, 0.1);
          border: 1px solid rgba(16, 185, 129, 0.2);
          padding: 8px 16px;
          border-radius: 100px;
          font-size: 0.875rem;
          color: #10b981;
        }

        .badge-dot {
          width: 8px;
          height: 8px;
          background: #10b981;
          border-radius: 50%;
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.2); }
        }

        .hero-headline {
          font-size: clamp(2.5rem, 5vw, 4rem);
          font-weight: 800;
          line-height: 1.1;
          letter-spacing: -0.03em;
          margin-bottom: 24px;
        }

        .hero-headline span {
          display: block;
        }

        .gradient-text {
          background: linear-gradient(135deg, #10b981 0%, #22d3ee 50%, #6366f1 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .hero-description {
          font-size: 1.125rem;
          color: rgba(255, 255, 255, 0.6);
          line-height: 1.7;
          max-width: 480px;
          margin-bottom: 40px;
        }

        .hero-cta-group {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
          margin-bottom: 48px;
        }

        .cta-glow {
          position: relative;
          background: linear-gradient(135deg, #10b981, #059669);
          border: none;
          padding: 16px 32px;
          border-radius: 16px;
          cursor: pointer;
          overflow: hidden;
          transition: all 0.3s;
        }

        .cta-glow:hover {
          transform: translateY(-3px);
          box-shadow: 0 20px 60px rgba(16, 185, 129, 0.4);
        }

        .cta-content {
          display: flex;
          align-items: center;
          gap: 10px;
          position: relative;
          z-index: 1;
        }

        .cta-text {
          font-size: 1rem;
          font-weight: 600;
          color: #fff;
        }

        .cta-arrow {
          font-size: 1.25rem;
          transition: transform 0.3s;
        }

        .cta-glow:hover .cta-arrow {
          transform: translateX(4px);
        }

        .cta-shimmer {
          position: absolute;
          inset: 0;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
          transform: translateX(-100%);
          animation: shimmer 3s infinite;
        }

        @keyframes shimmer {
          100% { transform: translateX(100%); }
        }

        .cta-outline {
          display: flex;
          align-items: center;
          gap: 10px;
          background: transparent;
          border: 1px solid rgba(255, 255, 255, 0.2);
          color: #fff;
          padding: 16px 24px;
          border-radius: 16px;
          font-size: 1rem;
          font-weight: 500;
          text-decoration: none;
          transition: all 0.3s;
        }

        .cta-outline svg {
          width: 20px;
          height: 20px;
        }

        .cta-outline:hover {
          background: rgba(255, 255, 255, 0.05);
          border-color: rgba(255, 255, 255, 0.3);
        }

        .hero-social-proof {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .avatars {
          display: flex;
        }

        .avatar {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          border: 2px solid #050a0f;
          margin-left: calc(var(--i) * -10px);
          overflow: hidden;
        }

        .avatar-inner {
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, #10b981, #6366f1);
        }

        .proof-text {
          display: flex;
          flex-direction: column;
        }

        .proof-number {
          font-weight: 700;
          font-size: 0.9rem;
        }

        .proof-label {
          font-size: 0.75rem;
          color: rgba(255, 255, 255, 0.5);
        }

        /* Terrarium Showcase */
        .hero-visual {
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .terrarium-showcase {
          position: relative;
          width: 400px;
          height: 450px;
          transition: transform 0.1s ease-out;
        }

        .showcase-glow {
          position: absolute;
          inset: -50px;
          background: radial-gradient(circle at center, rgba(16, 185, 129, 0.3) 0%, transparent 70%);
          filter: blur(40px);
          animation: glow-pulse 4s ease-in-out infinite;
        }

        @keyframes glow-pulse {
          0%, 100% { opacity: 0.5; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.1); }
        }

        .showcase-jar {
          position: relative;
          width: 280px;
          height: 350px;
          margin: 0 auto;
          background: linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%);
          border-radius: 0 0 140px 140px;
          border: 2px solid rgba(255, 255, 255, 0.15);
          overflow: hidden;
        }

        .showcase-jar::before {
          content: '';
          position: absolute;
          top: -30px;
          left: 50%;
          transform: translateX(-50%);
          width: 200px;
          height: 40px;
          background: linear-gradient(180deg, rgba(255,255,255,0.15), rgba(255,255,255,0.08));
          border-radius: 10px 10px 0 0;
          border: 2px solid rgba(255, 255, 255, 0.15);
          border-bottom: none;
        }

        .jar-reflection {
          position: absolute;
          top: 0;
          left: 10px;
          width: 30px;
          height: 80%;
          background: linear-gradient(180deg, rgba(255,255,255,0.2), transparent);
          border-radius: 20px;
        }

        .jar-content {
          position: absolute;
          inset: 0;
          overflow: hidden;
        }

        .mini-sky {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 50%;
          background: linear-gradient(180deg, #1a365d 0%, #2d4a7c 100%);
        }

        .mini-cloud {
          position: absolute;
          width: 60px;
          height: 20px;
          background: rgba(255, 255, 255, 0.3);
          border-radius: 20px;
          animation: drift 20s linear infinite;
        }

        .mini-cloud.c1 { top: 20%; left: -60px; animation-duration: 25s; }
        .mini-cloud.c2 { top: 35%; left: -60px; width: 40px; animation-duration: 18s; animation-delay: -5s; }

        @keyframes drift {
          from { transform: translateX(0); }
          to { transform: translateX(340px); }
        }

        .mini-ground {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 60px;
          background: linear-gradient(180deg, #2d1810 0%, #1a0f0a 100%);
        }

        .ground-moss {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 15px;
          background: linear-gradient(180deg, #228b22 0%, #1a5a1a 100%);
          border-radius: 0 0 10px 10px;
        }

        .mini-plant {
          position: absolute;
          bottom: 60px;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .mini-plant.p1 {
          left: 25%;
          animation: sway 4s ease-in-out infinite;
          transform-origin: bottom center;
        }

        .mini-plant.p2 {
          right: 25%;
          animation: sway 5s ease-in-out infinite;
          animation-delay: -1s;
          transform-origin: bottom center;
        }

        .mini-plant.p3 {
          left: 50%;
          transform: translateX(-50%);
          animation: sway 3.5s ease-in-out infinite;
          animation-delay: -2s;
          transform-origin: bottom center;
        }

        @keyframes sway {
          0%, 100% { transform: rotate(-2deg); }
          50% { transform: rotate(2deg); }
        }

        .mini-plant.p3 {
          animation: sway3 3.5s ease-in-out infinite;
        }

        @keyframes sway3 {
          0%, 100% { transform: translateX(-50%) rotate(-2deg); }
          50% { transform: translateX(-50%) rotate(2deg); }
        }

        .plant-stem {
          width: 6px;
          height: 50px;
          background: linear-gradient(180deg, #228b22, #1a5a1a);
          border-radius: 3px;
        }

        .plant-leaves {
          position: relative;
          display: flex;
          justify-content: center;
          margin-bottom: -10px;
        }

        .leaf {
          position: absolute;
          width: 25px;
          height: 12px;
          background: #2ecc71;
          border-radius: 50% 0 50% 0;
        }

        .leaf.l1 { transform: rotate(-45deg); left: -18px; top: 5px; }
        .leaf.l2 { transform: rotate(45deg); right: -18px; top: 5px; }
        .leaf.l3 { transform: rotate(-30deg); left: -12px; top: -8px; }

        .mushroom-cap {
          top: 0;
          width: 40px;
          height: 25px;
          background: linear-gradient(180deg, #e74c3c, #c0392b);
          border-radius: 50% 50% 30% 30%;
          position: relative;
          margin-bottom: -5px;
          z-index: 1;
        }

        .mushroom-cap::after {
          content: '';
          position: absolute;
          width: 8px;
          height: 6px;
          background: rgba(255,255,255,0.8);
          border-radius: 50%;
          top: 8px;
          left: 8px;
        }

        .mushroom-stem {
          width: 15px;
          height: 30px;
          background: linear-gradient(180deg, #f5deb3, #ddd6c3);
          border-radius: 3px 3px 5px 5px;
        }

        .flower-head {
          position: relative;
          width: 40px;
          height: 40px;
          margin-bottom: -5px;
        }

        .flower-petals {
          position: absolute;
          inset: 0;
        }

        .petal {
          position: absolute;
          width: 14px;
          height: 14px;
          background: #ff6b9d;
          border-radius: 50%;
          left: 50%;
          top: 50%;
          transform-origin: center;
          transform: translate(-50%, -50%) rotate(calc(var(--i) * 72deg)) translateY(-12px);
        }

        .flower-center {
          position: absolute;
          width: 14px;
          height: 14px;
          background: #ffd93d;
          border-radius: 50%;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          z-index: 2;
        }

        .flower-stem {
          width: 5px;
          height: 40px;
          background: linear-gradient(180deg, #228b22, #1a5a1a);
          border-radius: 2px;
        }

        /* Mini Insects in Hero */
        .mini-insect {
          position: absolute;
          display: flex;
          align-items: center;
          gap: 0;
        }

        .mini-insect.insect-butterfly {
          top: 30%;
          right: 20%;
          animation: insect-fly 5s ease-in-out infinite;
        }

        .mini-insect.insect-ladybug {
          bottom: 70px;
          left: 30%;
          animation: insect-walk 4s ease-in-out infinite;
          animation-delay: -1.5s;
        }

        .mini-insect-body {
          width: 4px;
          height: 10px;
          background: #333;
          border-radius: 2px;
        }

        .mini-insect-wing {
          width: 10px;
          height: 8px;
          background: rgba(236, 72, 153, 0.6);
          border-radius: 50%;
          animation: mini-wing-flap 0.5s ease-in-out infinite;
        }

        .mini-insect-wing.wing-r {
          animation-delay: 0.05s;
        }

        .mini-ladybug-body {
          width: 12px;
          height: 10px;
          background: #e53935;
          border-radius: 50%;
          position: relative;
        }

        .mini-ladybug-spot {
          position: absolute;
          width: 3px;
          height: 3px;
          background: #222;
          border-radius: 50%;
          top: 3px;
          left: 2px;
        }

        .mini-ladybug-spot.s2 {
          left: auto;
          right: 2px;
        }

        @keyframes insect-fly {
          0%, 100% { transform: translate(0, 0); }
          25% { transform: translate(15px, -10px); }
          50% { transform: translate(-10px, -5px); }
          75% { transform: translate(5px, 5px); }
        }

        @keyframes insect-walk {
          0%, 100% { transform: translateX(0); }
          50% { transform: translateX(20px); }
        }

        @keyframes mini-wing-flap {
          0%, 100% { transform: scaleX(1); }
          50% { transform: scaleX(0.6); }
        }

        /* Mini Chat Bubbles in Hero */
        .mini-chat-bubble {
          position: absolute;
          background: rgba(0, 0, 0, 0.75);
          color: #fff;
          padding: 4px 8px;
          border-radius: 8px;
          font-size: 0.55rem;
          white-space: nowrap;
          pointer-events: none;
          animation: bubble-pop 4s ease-in-out infinite;
        }

        .mini-chat-bubble::after {
          content: '';
          position: absolute;
          bottom: -4px;
          left: 50%;
          transform: translateX(-50%);
          border: 3px solid transparent;
          border-top-color: rgba(0, 0, 0, 0.75);
        }

        .mini-chat-bubble.bubble-1 {
          bottom: 180px;
          left: 22%;
          animation-delay: 0s;
        }

        .mini-chat-bubble.bubble-2 {
          top: 25%;
          right: 15%;
          animation-delay: 2s;
        }

        @keyframes bubble-pop {
          0%, 15% { opacity: 0; transform: scale(0.7) translateY(5px); }
          20%, 80% { opacity: 1; transform: scale(1) translateY(0); }
          85%, 100% { opacity: 0; transform: scale(0.7) translateY(-5px); }
        }

        .floating-labels {
          position: absolute;
          inset: 0;
          pointer-events: none;
        }

        .label {
          position: absolute;
          display: flex;
          align-items: center;
          gap: 8px;
          background: rgba(0, 0, 0, 0.8);
          backdrop-filter: blur(10px);
          padding: 8px 14px;
          border-radius: 100px;
          font-size: 0.75rem;
          white-space: nowrap;
          animation: label-float 3s ease-in-out infinite;
        }

        .label-1 { top: 15%; right: -20px; animation-delay: 0s; }
        .label-2 { top: 45%; left: -30px; animation-delay: 1s; }
        .label-3 { bottom: 25%; right: -10px; animation-delay: 2s; }

        @keyframes label-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }

        .label-icon {
          font-size: 1rem;
        }

        .label-text {
          color: rgba(255, 255, 255, 0.9);
        }

        /* Stats Bar */
        .stats-bar {
          position: relative;
          z-index: 2;
          padding: 40px 24px;
          background: rgba(255, 255, 255, 0.02);
          border-top: 1px solid rgba(255, 255, 255, 0.05);
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
        }

        .stats-container {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 48px;
          flex-wrap: wrap;
          max-width: 900px;
          margin: 0 auto;
        }

        .stat-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
        }

        .stat-number {
          font-size: 2rem;
          font-weight: 800;
          background: linear-gradient(135deg, #10b981, #22d3ee);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .stat-label {
          font-size: 0.875rem;
          color: rgba(255, 255, 255, 0.5);
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .stat-divider {
          width: 1px;
          height: 40px;
          background: rgba(255, 255, 255, 0.1);
        }

        /* Features Bento Grid */
        .features-bento {
          position: relative;
          z-index: 2;
          padding: 100px 24px;
          max-width: 1200px;
          margin: 0 auto;
        }

        .section-intro {
          text-align: center;
          margin-bottom: 60px;
        }

        .section-tag {
          display: inline-block;
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
          padding: 6px 16px;
          border-radius: 100px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          margin-bottom: 16px;
        }

        .section-title {
          font-size: clamp(2rem, 4vw, 3rem);
          font-weight: 800;
          letter-spacing: -0.03em;
          margin-bottom: 16px;
        }

        .section-subtitle {
          font-size: 1.125rem;
          color: rgba(255, 255, 255, 0.5);
          max-width: 500px;
          margin: 0 auto;
        }

        .bento-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
        }

        .bento-card {
          position: relative;
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 24px;
          padding: 32px;
          overflow: hidden;
          transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .bento-card:hover {
          transform: translateY(-5px);
          border-color: rgba(16, 185, 129, 0.3);
          background: rgba(255, 255, 255, 0.05);
        }

        .card-glow {
          position: absolute;
          top: 0;
          right: 0;
          width: 150px;
          height: 150px;
          opacity: 0.1;
          filter: blur(60px);
          transition: opacity 0.4s;
        }

        .bento-card:hover .card-glow {
          opacity: 0.2;
        }

        .card-content {
          position: relative;
          z-index: 1;
        }

        .card-icon {
          font-size: 2.5rem;
          margin-bottom: 20px;
          display: block;
        }

        .card-title {
          font-size: 1.25rem;
          font-weight: 700;
          margin-bottom: 12px;
        }

        .card-description {
          color: rgba(255, 255, 255, 0.6);
          line-height: 1.6;
        }

        .card-commands {
          grid-column: span 2;
        }

        .card-plants {
          grid-column: span 2;
        }

        .command-list {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
          margin-top: 20px;
        }

        .command-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
          background: rgba(0, 0, 0, 0.3);
          padding: 12px 16px;
          border-radius: 12px;
        }

        .command-item code {
          color: #10b981;
          font-family: 'SF Mono', 'Monaco', monospace;
          font-size: 0.9rem;
        }

        .command-item span {
          color: rgba(255, 255, 255, 0.5);
          font-size: 0.8rem;
        }

        .plant-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 20px;
        }

        .plant-tag {
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
          padding: 6px 12px;
          border-radius: 100px;
          font-size: 0.75rem;
          font-weight: 500;
        }

        .plant-tag.insect-tag {
          background: rgba(245, 158, 11, 0.1);
          color: #f59e0b;
        }

        /* How It Works */
        .how-section {
          position: relative;
          z-index: 2;
          padding: 100px 24px;
          max-width: 900px;
          margin: 0 auto;
        }

        .steps-timeline {
          position: relative;
          display: flex;
          flex-direction: column;
          gap: 40px;
        }

        .timeline-line {
          position: absolute;
          left: 24px;
          top: 0;
          bottom: 0;
          width: 2px;
          background: rgba(255, 255, 255, 0.1);
        }

        .line-progress {
          width: 100%;
          height: 60%;
          background: linear-gradient(180deg, #10b981, transparent);
        }

        .step-card {
          display: flex;
          gap: 32px;
          align-items: flex-start;
        }

        .step-number {
          flex-shrink: 0;
          width: 50px;
          height: 50px;
          background: linear-gradient(135deg, #10b981, #059669);
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1rem;
          font-weight: 800;
          position: relative;
          z-index: 1;
        }

        .step-content {
          flex: 1;
          padding-top: 4px;
        }

        .step-icon {
          width: 48px;
          height: 48px;
          background: rgba(16, 185, 129, 0.1);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 16px;
        }

        .step-icon svg {
          width: 24px;
          height: 24px;
          color: #10b981;
        }

        .step-content h3 {
          font-size: 1.25rem;
          font-weight: 700;
          margin-bottom: 8px;
        }

        .step-content p {
          color: rgba(255, 255, 255, 0.6);
          line-height: 1.6;
        }

        /* CTA Section */
        .cta-section-2026 {
          position: relative;
          z-index: 2;
          padding: 60px 24px 100px;
        }

        .cta-card-2026 {
          position: relative;
          max-width: 800px;
          margin: 0 auto;
          background: linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(99, 102, 241, 0.1));
          border: 1px solid rgba(16, 185, 129, 0.2);
          border-radius: 32px;
          padding: 80px 40px;
          text-align: center;
          overflow: hidden;
        }

        .cta-bg-pattern {
          position: absolute;
          inset: 0;
          background-image: radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px);
          background-size: 24px 24px;
        }

        .cta-content-2026 {
          position: relative;
          z-index: 1;
        }

        .cta-content-2026 h2 {
          font-size: clamp(1.5rem, 4vw, 2.5rem);
          font-weight: 800;
          margin-bottom: 12px;
          letter-spacing: -0.02em;
        }

        .cta-content-2026 p {
          color: rgba(255, 255, 255, 0.6);
          margin-bottom: 32px;
          font-size: 1.125rem;
        }

        .cta-mega {
          display: inline-flex;
          align-items: center;
          gap: 12px;
          background: linear-gradient(135deg, #10b981, #059669);
          border: none;
          padding: 20px 40px;
          border-radius: 100px;
          cursor: pointer;
          transition: all 0.3s;
        }

        .cta-mega:hover {
          transform: translateY(-3px) scale(1.02);
          box-shadow: 0 20px 60px rgba(16, 185, 129, 0.4);
        }

        .cta-mega-icon {
          font-size: 1.5rem;
        }

        .cta-mega-text {
          font-size: 1.125rem;
          font-weight: 700;
          color: #fff;
        }

        /* FAQ Section */
        .faq-section {
          position: relative;
          z-index: 2;
          padding: 100px 24px;
          max-width: 900px;
          margin: 0 auto;
        }

        .faq-grid {
          display: grid;
          gap: 16px;
        }

        .faq-item {
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.06);
          border-radius: 16px;
          padding: 24px 28px;
          transition: border-color 0.3s;
        }

        .faq-item:hover {
          border-color: rgba(16, 185, 129, 0.2);
        }

        .faq-item h3 {
          font-size: 1rem;
          font-weight: 600;
          margin-bottom: 8px;
          color: #fff;
        }

        .faq-item p {
          font-size: 0.9rem;
          color: rgba(255, 255, 255, 0.6);
          line-height: 1.6;
          margin: 0;
        }

        /* Footer */
        .footer-2026 {
          position: relative;
          z-index: 2;
          padding: 32px 24px;
          border-top: 1px solid rgba(255, 255, 255, 0.05);
        }

        .footer-content-2026 {
          display: flex;
          justify-content: space-between;
          align-items: center;
          max-width: 1200px;
          margin: 0 auto;
          flex-wrap: wrap;
          gap: 16px;
        }

        .footer-brand {
          display: flex;
          align-items: center;
          gap: 10px;
          color: rgba(255, 255, 255, 0.7);
          font-weight: 600;
        }

        .footer-links {
          display: flex;
          align-items: center;
          gap: 12px;
          color: rgba(255, 255, 255, 0.5);
          font-size: 0.875rem;
        }

        .footer-dot {
          color: rgba(255, 255, 255, 0.2);
        }

        .footer-legal-link {
          color: rgba(255, 255, 255, 0.4);
          text-decoration: none;
          font-size: 0.875rem;
          transition: color 0.2s;
        }

        .footer-legal-link:hover {
          color: rgba(255, 255, 255, 0.8);
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .hero-2026 {
            grid-template-columns: 1fr;
            text-align: center;
            padding: 140px 24px 60px;
          }

          .hero-description {
            margin: 0 auto 40px;
          }

          .hero-cta-group {
            justify-content: center;
          }

          .hero-social-proof {
            justify-content: center;
          }

          .hero-visual {
            order: -1;
            margin-bottom: 40px;
          }

          .terrarium-showcase {
            width: 300px;
            height: 350px;
          }

          .showcase-jar {
            width: 220px;
            height: 280px;
          }

          .bento-grid {
            grid-template-columns: 1fr 1fr;
          }

          .card-commands {
            grid-column: span 2;
          }

          .card-plants {
            grid-column: span 2;
          }
        }

        @media (max-width: 768px) {
          .nav-glass {
            padding: 10px 16px;
          }

          .nav-link {
            display: none;
          }

          .bento-grid {
            grid-template-columns: 1fr;
          }

          .card-commands,
          .card-plants {
            grid-column: span 1;
          }

          .command-list {
            grid-template-columns: 1fr;
          }

          .stats-container {
            gap: 24px;
          }

          .stat-divider {
            display: none;
          }

          .floating-labels .label {
            display: none;
          }

          .footer-content-2026 {
            flex-direction: column;
            text-align: center;
          }
        }
      `}</style>
    </div>
  )
}

export default Landing